import type {
  BooleanAnswer,
  MultipleChoiceAnswer,
  OpenTextAnswer,
  RatingAnswer,
  SurveyResponsesExport,
} from '@voice2me/shared';

/**
 * Turns one stored answer envelope into the plain cell text a spreadsheet expects — the same
 * readable value a reader would see in the survey, not the raw option ids we store. Multi-select
 * answers are joined with a comma, matching how Google Forms lays a checkbox question into a sheet.
 */
function formatAnswer(
  question: SurveyResponsesExport['questions'][number],
  value: unknown,
): string {
  if (value == null) return '';

  switch (question.type) {
    case 'multiple_choice': {
      const labels = new Map((question.options ?? []).map((option) => [option.id, option.label]));
      return (value as MultipleChoiceAnswer).optionIds
        .map((id) => labels.get(id) ?? id)
        .join(', ');
    }
    case 'boolean':
      return (value as BooleanAnswer).value ? 'Yes' : 'No';
    case 'rating':
      return String((value as RatingAnswer).value);
    case 'open_text':
      return (value as OpenTextAnswer).text;
  }
}

/** A filesystem-friendly workbook name derived from the survey slug. */
function fileName(slug: string): string {
  const safe = slug.replace(/[^a-z0-9-]+/gi, '-').replace(/^-+|-+$/g, '') || 'survey';
  return `${safe}-responses.xlsx`;
}

/**
 * Builds and downloads an Excel workbook of a survey's responses: one column per question (the
 * question text as the header) and one row per submission — the spreadsheet shape people expect
 * from Google Forms. SheetJS is loaded on demand so it stays out of the initial admin bundle.
 */
export async function downloadResponsesWorkbook(data: SurveyResponsesExport): Promise<void> {
  const XLSX = await import('xlsx');

  const header = data.questions.map((question) => question.prompt);
  const body = data.rows.map((row) =>
    data.questions.map((question) => formatAnswer(question, row.answers[question.id])),
  );

  const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Responses');
  XLSX.writeFile(workbook, fileName(data.survey.slug));
}
